import { useRef, useState } from 'react'
import {
  getOpticalMeasureDistanceRange,
  type FaceData,
  type OpticalMeasureFramingDistanceProfileSettings,
  type OpticalMeasureSHCaptureSettings,
  type OpticalMeasureSHCoreMLModelVariant,
  type SHCaptureData,
  type ShDebug,
  type SHQualityDebug,
  type ShScaleDebug,
} from '../index'
import { ROTATION_X_THRESHOLD, ROTATION_Y_THRESHOLD } from './constants'
import { Phase } from './types'

type CandidateDebugSummary = {
  confidence?: number
  score?: number
  luma?: number
  fitResidual?: number
  usedArcFit?: boolean
}

export type ShDebugSummary = {
  coremlModel?: OpticalMeasureSHCoreMLModelVariant
  coremlInputSize?: number
  coremlModelFallback?: boolean
  cropWidth?: number
  cropHeight?: number
  cropScreenWidth?: number
  cropScreenHeight?: number
  cropAspectRatio?: number
  targetAspectRatio?: number
  cropAspectPreserved?: boolean
  isFinalFrameModel?: boolean
  confidence?: number
  quality?: number
  qualityDebug?: SHQualityDebug
  selectedContourType?: ShDebug['selectedContourType']
  measurementAccepted?: boolean
  measurementRejectionReason?: string
  rejectionReason?: string
  acceptedCandidateCount?: number
  rejectedBrightContourCount?: number
  otherContourCount?: number
  scale?: ShScaleDebug
  left?: CandidateDebugSummary
  right?: CandidateDebugSummary
}

export type SHCaptureState = {
  phase: Phase
  capturedCount: number
  targetCount: number
  progress: number
  nativeState: SHCaptureData | null
  start: () => void
  stop: () => void
  clear: () => void
  applyNativeState: (state: SHCaptureData | undefined) => void
  handleFaceData: (incoming: FaceData) => void
}

const isCentered = (incoming: FaceData) =>
  Math.abs(incoming.rotationX) < ROTATION_X_THRESHOLD &&
  Math.abs(incoming.rotationY) < ROTATION_Y_THRESHOLD

const hasOpenEyes = (
  incoming: FaceData,
  capture: OpticalMeasureSHCaptureSettings
) =>
  (incoming.eyeOpenness?.left ?? 0) >= capture.eyeOpenThreshold &&
  (incoming.eyeOpenness?.right ?? 0) >= capture.eyeOpenThreshold

export function useSHCapture(
  capture: OpticalMeasureSHCaptureSettings,
  distanceProfile: OpticalMeasureFramingDistanceProfileSettings
): SHCaptureState {
  const targetCandidateFrames = Math.max(
    1,
    Math.round(capture.targetCandidateFrames)
  )
  const [phase, setPhase] = useState<Phase>('idle')
  const [capturedCount, setCapturedCount] = useState(0)
  const [targetCount, setTargetCount] = useState(targetCandidateFrames)
  const [nativeState, setNativeState] = useState<SHCaptureData | null>(null)

  const activeRef = useRef(false)
  const captureRef = useRef(capture)
  const distanceProfileRef = useRef(distanceProfile)

  const start = () => {
    captureRef.current = capture
    distanceProfileRef.current = distanceProfile
    activeRef.current = true
    setCapturedCount(0)
    setTargetCount(targetCandidateFrames)
    setNativeState(null)
    setPhase('findingFrame')
  }

  const stop = () => {
    activeRef.current = false
    setPhase('idle')
  }

  const clear = () => {
    activeRef.current = false
    setCapturedCount(0)
    setTargetCount(targetCandidateFrames)
    setNativeState(null)
    setPhase('idle')
  }

  // Native hard-gate rejections (SHMeasurementStrategy pre-detection gates)
  // mapped to guidance phases. JS thresholds are stricter and usually fire
  // first; this is the fallback when native rejected inside the JS margin.
  const phaseForNativeRejection = (
    reason: string | undefined,
    incoming?: FaceData
  ): Phase | null => {
    if (reason === 'eyes-closed') return 'opening'
    if (reason === 'pose-out-of-range') return 'positioning'
    if (reason === 'distance-out-of-range') {
      if (!incoming) return 'positioning'
      return incoming.distanceCm <
        distanceProfileRef.current.preferredDistanceCm
        ? 'tooClose'
        : 'tooFar'
    }
    return null
  }

  const mapNativePhase = (state: SHCaptureData, incoming?: FaceData): Phase => {
    if (state.phase === 'idle') return 'idle'
    if (state.phase === 'calibrating') return 'calibrating'
    if (state.phase === 'complete') return 'complete'
    if (state.phase === 'failed') return 'failed'
    if (state.phase === 'capturing') return 'sampling'

    if (!incoming?.detected) return 'detecting'
    const distanceRange = getOpticalMeasureDistanceRange(
      distanceProfileRef.current,
      'guidance'
    )
    if (incoming.distanceCm < distanceRange.min) return 'tooClose'
    if (incoming.distanceCm > distanceRange.max) return 'tooFar'
    if (!isCentered(incoming)) return 'positioning'
    if (!hasOpenEyes(incoming, captureRef.current)) return 'opening'
    return (
      phaseForNativeRejection(state.rejectionReason, incoming) ?? 'findingFrame'
    )
  }

  const applyNativeState = (
    state: SHCaptureData | undefined,
    incoming?: FaceData
  ) => {
    if (!state) return

    activeRef.current = state.active
    setNativeState(state)
    setCapturedCount(state.capturedCount)
    setTargetCount(Math.max(1, state.targetCount))
    setPhase(mapNativePhase(state, incoming))
  }

  const handleFaceData = (incoming: FaceData) => {
    if (incoming.shCapture) {
      applyNativeState(incoming.shCapture, incoming)
      return
    }

    if (!activeRef.current) return

    if (incoming.calibrating) {
      setPhase('calibrating')
      return
    }

    if (!incoming.detected) {
      setPhase('detecting')
      return
    }

    const distanceRange = getOpticalMeasureDistanceRange(
      distanceProfileRef.current,
      'guidance'
    )

    if (incoming.distanceCm < distanceRange.min) {
      setPhase('tooClose')
      return
    }

    if (incoming.distanceCm > distanceRange.max) {
      setPhase('tooFar')
      return
    }

    if (!isCentered(incoming)) {
      setPhase('positioning')
      return
    }

    if (!hasOpenEyes(incoming, captureRef.current)) {
      setPhase('opening')
      return
    }

    setPhase('findingFrame')
  }

  return {
    phase,
    capturedCount,
    targetCount,
    progress: capturedCount / targetCount,
    nativeState,
    start,
    stop,
    clear,
    applyNativeState,
    handleFaceData,
  }
}
